import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

export default function DataSourcesPage() {
  const { language } = useLanguage();
  const hi = language === 'hi';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50" data-testid="data-sources-page">
      {/* Nav */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-home">
            <img src="https://customer-assets.emergentagent.com/job_b71b88a0-de31-4e82-b047-c99df2477168/artifacts/7dlw52ts_logo%20%281%29.jpg" alt="Logo" className="h-10 w-10 rounded-lg" />
            <span className="font-bold text-lg bg-gradient-to-r from-orange-500 to-green-600 bg-clip-text text-transparent" style={{fontFamily: 'Outfit, sans-serif'}}>शिक्षा मित्र</span>
          </Link>
          <Link to="/my-school" className="px-4 py-2 bg-orange-500 text-white rounded-full text-sm font-semibold hover:bg-orange-600" data-testid="nav-check-school">
            {hi ? 'अपनी School Check करें' : 'Check Your School'}
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-4 md:px-8 pt-16 pb-10 text-center max-w-4xl mx-auto">
        <span className="inline-block px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-bold uppercase tracking-wider mb-4">
          🛡️ {hi ? 'पारदर्शिता (Transparency)' : 'Transparency'}
        </span>
        <h1 className="text-4xl md:text-6xl font-black text-slate-900 tracking-tight mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>
          {hi ? (<>हमारा Data <span className="bg-gradient-to-r from-orange-500 to-green-600 bg-clip-text text-transparent">कहाँ से आया?</span></>) : (<>Where Does Our <span className="bg-gradient-to-r from-orange-500 to-green-600 bg-clip-text text-transparent">Data Come From?</span></>)}
        </h1>
        <p className="text-slate-600 text-lg md:text-xl max-w-2xl mx-auto" style={{fontFamily: 'Manrope, sans-serif'}}>
          {hi
            ? 'हम 4.48 लाख+ schools की जानकारी रखते हैं — पर यह सब सिर्फ public records से है। आपकी privacy हमारी ज़िम्मेदारी है।'
            : 'We hold data on 4.48L+ schools — all from public government records. Your privacy is our responsibility.'}
        </p>
      </section>

      {/* Sources */}
      <section className="px-4 md:px-8 pb-12 max-w-5xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>
          📚 {hi ? 'हमारे Data Sources (सरकारी / सार्वजनिक)' : 'Our Data Sources (Government / Public)'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { icon: '🏛️', title: 'UDISE+ Portal', url: 'https://udiseplus.gov.in', hi: 'भारत सरकार, MoE — हर school का 11-digit unique code। यह राष्ट्रीय शिक्षा management information system है।', en: 'Government of India, MoE — every school has an 11-digit UDISE code. National Education MIS.' },
            { icon: '🏙️', title: 'Municipal Corporation Records', url: null, hi: 'Indore Nagar Nigam जैसी संस्थाओं द्वारा published private school lists (ward-wise) — सार्वजनिक disclosure।', en: 'Public lists published by municipal bodies like Indore Nagar Nigam (ward-wise private schools).' },
            { icon: '🎓', title: 'CBSE / ICSE / State Board', url: 'https://cbse.gov.in', hi: 'Affiliated schools की public directory — board की website पर available है।', en: 'Public directory of affiliated schools — available on respective board websites.' },
            { icon: '📍', title: 'OpenStreetMap / Public GPS', url: null, hi: 'Geo-coordinates (latitude/longitude) सार्वजनिक map services से।', en: 'Geo-coordinates from public map services (OSM, Google).' },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition" data-testid={`data-source-${i}`}>
              <div className="flex items-start gap-4">
                <span className="text-4xl flex-shrink-0">{s.icon}</span>
                <div className="flex-1">
                  <h3 className="font-bold text-lg text-slate-900 mb-1">{s.title}</h3>
                  <p className="text-sm text-slate-600">{hi ? s.hi : s.en}</p>
                  {s.url && (
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs text-orange-600 underline hover:text-orange-700 mt-2 inline-block">
                      {s.url} →
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Have / Don't Have */}
      <section className="px-4 md:px-8 pb-12 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6" data-testid="what-we-have">
            <h3 className="font-black text-green-900 mb-3 text-xl">✅ {hi ? 'हमारे पास क्या है' : 'What We Have'}</h3>
            <ul className="space-y-2 text-sm text-green-900">
              {[
                hi ? 'School का नाम, पता, GPS location' : 'School name, address, GPS location',
                hi ? 'UDISE code, board, school category' : 'UDISE code, board, school category',
                hi ? 'Principal/representative का नाम और phone (public listing से)' : 'Principal/representative name and phone (from public listing)',
                hi ? 'Established year, school type' : 'Established year, school type',
                hi ? 'Student & teacher counts (UDISE+ से aggregate)' : 'Student & teacher counts (aggregate from UDISE+)',
              ].map((t, i) => <li key={i} className="flex gap-2"><span>•</span><span>{t}</span></li>)}
            </ul>
          </div>
          <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6" data-testid="what-we-dont-have">
            <h3 className="font-black text-red-900 mb-3 text-xl">❌ {hi ? 'हमारे पास क्या नहीं है' : "What We Don't Have"}</h3>
            <ul className="space-y-2 text-sm text-red-900">
              {[
                hi ? 'किसी student का व्यक्तिगत data या photo' : 'Any individual student data or photo',
                hi ? 'आपकी school की fees structure या financial records' : 'Your school fee structure or financial records',
                hi ? 'Internal staff details, salaries, HR records' : 'Internal staff details, salaries, HR records',
                hi ? 'Attendance, exam marks, या कोई private record' : 'Attendance, exam marks, or any private record',
                hi ? 'सब private data सिर्फ तब आएगा जब आप platform पर sign up करेंगे — और आपके ही control में रहेगा' : 'All private data comes ONLY when you sign up — and stays under your control',
              ].map((t, i) => <li key={i} className="flex gap-2"><span>•</span><span>{t}</span></li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Your Control */}
      <section className="px-4 md:px-8 pb-16 max-w-5xl mx-auto">
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-8 md:p-12 text-white shadow-2xl" data-testid="control-section">
          <h2 className="text-3xl md:text-4xl font-black mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>
            🔐 {hi ? 'आपकी information पर आपका control' : 'You Control Your Information'}
          </h2>
          <p className="text-slate-300 text-base md:text-lg mb-6 max-w-3xl">
            {hi
              ? 'अगर आप नहीं चाहते कि आपकी school हमारे public listing में दिखे, तो एक click में remove कर सकते हैं। कोई question नहीं, कोई paperwork नहीं।'
              : "If you don't want your school to appear in our public listing, remove it in one click. No questions, no paperwork."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/my-school" className="px-6 py-3 bg-orange-500 text-white rounded-full font-bold hover:bg-orange-600 transition text-center" data-testid="btn-find-school">
              🔍 {hi ? 'अपनी School Find करें' : 'Find Your School'}
            </Link>
            <Link to="/my-school?action=remove" className="px-6 py-3 bg-white/10 backdrop-blur border border-white/30 text-white rounded-full font-bold hover:bg-white/20 transition text-center" data-testid="btn-remove-school">
              🗑️ {hi ? 'School Remove करें' : 'Remove My School'}
            </Link>
          </div>
          <p className="text-xs text-slate-400 mt-5">
            {hi
              ? 'Verification के लिए Principal/Owner की phone और designation ज़रूरी है। 24-48 घंटे में process हो जाएगा।'
              : 'Principal/Owner phone & designation required for verification. Processed within 24-48 hours.'}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-6 bg-white/50">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center text-sm text-slate-500">
          <div className="flex gap-4 justify-center flex-wrap mb-2">
            <Link to="/privacy" className="hover:text-orange-600">Privacy Policy</Link>
            <span>·</span>
            <Link to="/terms" className="hover:text-orange-600">Terms</Link>
            <span>·</span>
            <Link to="/data-sources" className="hover:text-orange-600 font-semibold">Data Sources</Link>
            <span>·</span>
            <Link to="/contact" className="hover:text-orange-600">Contact</Link>
          </div>
          <p>© 2026 System Of Success · शिक्षा मित्र</p>
        </div>
      </footer>
    </div>
  );
}
